<template>
  <ValidationObserver
    v-slot="{ handleSubmit, invalid }"
    ref="form"
    slim
  >
    <form
      class="form-horizontal"
      name="addCreditCardForm"
      novalidate
      @submit.prevent="handleSubmit(onSubmit)"
    >
      <Field
        :label="$t('ACCOUNT_BILLING_ADD_CC_CURRENT_ADDRESS')"
        :has-tooltip="true"
        :tooltip-text="$t('ACCOUNT_BILLING_ADD_CC_CURRENT_ADDRESS_TT')"
      >
        <template #element>
          <div class="col-sm-9">
            <span v-html="currentAddressFormatted" />
          </div>
        </template>
      </Field>
      <Field
        :label="$t('ACCOUNT_BILLING_ADD_CC_ADDRESS')"
        :has-tooltip="true"
        :tooltip-text="$t('ACCOUNT_BILLING_ADD_CC_ADDRESS_TT')"
      >
        <template #element>
          <div class="col-sm-9">
            <nzToggle
              data-testid="new-address-toggle"
              v-model="useNewAddress"
              @onToggle="toggleUseNewAddress"
            />
          </div>
        </template>
      </Field>
      <fieldset v-if="useNewAddress">
        <Field
          :label="$t('ACCOUNT_BILLING_ADD_CC_COUNTRY')"
          :has-tooltip="true"
          :tooltip-text="$t('ACCOUNT_BILLING_ADD_CC_COUNTRY_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="cardCountry"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-5 col-md-4">
                  <CountrySelector
                    data-testid="card-country"
                    v-model="countryValue"
                    :placeholder="$t('ACCOUNT_BILLING_ADD_CC_COUNTRY_DEFAULT')"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="$t('ACCOUNT_BILLING_ADD_CC_NEW_ADDRESS')"
          :has-tooltip="true"
          :tooltip-text="$t('ACCOUNT_BILLING_ADD_CC_NEW_ADDRESS_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="streetAddress"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-5 col-md-4">
                  <input
                    v-model="newCreditCard.creditCardAddress.streetAddress"
                    name="streetAddress"
                    type="text"
                    class="form-control"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <Field
          label=""
          :has-tooltip="false"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="addressLine2"
              rules=""
              slim
            >
              <div :class="classes">
                <div class="col-sm-5 col-md-4">
                  <input
                    v-model="newCreditCard.creditCardAddress.addressLine2"
                    name="addressLine2"
                    type="text"
                    class="form-control"
                    :placeholder="$t('ACCOUNT_BILLING_ADD_CC_NEW_ADDRESS_TWO_DEFAULT')"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="$t('ACCOUNT_BILLING_ADD_CC_NEW_CITY')"
          :has-tooltip="true"
          :tooltip-text="$t('ACCOUNT_BILLING_ADD_CC_NEW_CITY_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="city"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-5 col-md-4">
                  <input
                    v-model="newCreditCard.creditCardAddress.city"
                    name="city"
                    type="text"
                    class="form-control"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="newCreditCard.creditCardAddress.countryCode === 'US' ? $t('ACCOUNT_BILLING_ADD_CC_NEW_STATE') : $t('ACCOUNT_BILLING_ADD_CC_NEW_REGION')"
          :has-tooltip="true"
          :tooltip-text="newCreditCard.creditCardAddress.countryCode === 'US' ? $t('ACCOUNT_BILLING_ADD_CC_NEW_STATE_TT') : $t('ACCOUNT_BILLING_ADD_CC_NEW_REGION_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="region"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-5 col-md-4">
                  <SearchableDropdown
                    v-if="newCreditCard.creditCardAddress.countryCode === 'US'"
                    data-testid="card-region-select"
                    v-model="newCreditCard.creditCardAddress.region"
                    :config="stateOptions"
                    :placeholder="$t('ACCOUNT_BILLING_ADD_CC_NEW_STATE_DEFAULT')"
                  />
                  <input
                    v-else
                    v-model="newCreditCard.creditCardAddress.region"
                    name="region"
                    type="text"
                    class="form-control"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>
        <Field
          :label="newCreditCard.creditCardAddress.countryCode === 'US' ? $t('ACCOUNT_BILLING_ADD_CC_NEW_ZIP') : $t('ACCOUNT_BILLING_ADD_CC_NEW_POSTAL_CODE')"
          :has-tooltip="true"
          :tooltip-text="newCreditCard.creditCardAddress.countryCode === 'US' ? $t('ACCOUNT_BILLING_ADD_CC_NEW_ZIP_TT') : $t('ACCOUNT_BILLING_ADD_CC_NEW_POSTAL_CODE_TT')"
        >
          <template #element>
            <ValidationProvider
              v-slot="{ classes }"
              ref="postalCode"
              rules="required"
              slim
            >
              <div :class="classes">
                <div class="col-sm-2">
                  <input
                    v-model="newCreditCard.creditCardAddress.postalCode"
                    name="postalCode"
                    type="text"
                    class="form-control"
                  />
                </div>
              </div>
            </ValidationProvider>
          </template>
        </Field>
      </fieldset>
      <Field
        :label="$t('ACCOUNT_BILLING_ADD_CC_CC_NAME')"
        :has-tooltip="true"
        :tooltip-text="$t('ACCOUNT_BILLING_ADD_CC_CC_NAME_TT')"
      >
        <template #element>
          <ValidationProvider
            v-slot="{ classes }"
            ref="cardName"
            rules="required"
            slim
          >
            <div :class="classes">
              <div class="col-sm-5 col-md-4">
                <input
                  v-model="newCreditCard.cardName"
                  name="cardName"
                  type="text"
                  class="form-control"
                />
              </div>
            </div>
          </ValidationProvider>
        </template>
      </Field>
      <Field
        :label="$t('ACCOUNT_BILLING_ADD_CC_CUST_NAME')"
        :has-tooltip="true"
        :tooltip-text="$t('ACCOUNT_BILLING_ADD_CC_CUST_NAME_TT')"
      >
        <template #element>
          <ValidationProvider
            v-slot="{ classes }"
            ref="customerNameOnCard"
            rules="required"
            slim
          > 
            <div :class="classes">
              <div class="col-sm-5 col-md-4">
                <input
                  v-model="newCreditCard.customerNameOnCard"
                  name="customerNameOnCard"
                  type="text"
                  class="form-control"
                />
              </div>
            </div>
          </ValidationProvider>
        </template>
      </Field>
      <Field
        :label="$t('ACCOUNT_BILLING_ADD_CC_CC_NUM')"
        :has-tooltip="true"
        :tooltip-text="$t('ACCOUNT_BILLING_ADD_CC_CC_NUM_TT')"
      >
        <template #element>
          <ValidationProvider
            v-slot="{ classes }"
            ref="creditCardNumber"
            rules="required|luhn"
            mode="lazy"
            slim
          > 
            <div :class="classes">
              <div class="col-sm-5 col-md-4 credit-card-input">
                <input
                  v-model="newCreditCard.creditCardNumber"
                  name="creditCardNumber"
                  type="text"
                  class="form-control"
                  v-mask="cardNumberMask"
                />
                <i class="credit-card-img" :class="currentCardType"></i>
              </div>
            </div>
          </ValidationProvider>
        </template>
      </Field>
      <div class="form-group">
        <label class="control-label col-md-3 col-sm-3">
          {{ $t('ACCOUNT_BILLING_ADD_CC_CC_EXP') }}
          <i
            v-tooltip.right="$t('ACCOUNT_BILLING_ADD_CC_CC_EXP_TT')"
            class="fa fa-question-circle"
          />
        </label>

        <div class="col-sm-2">
          <SearchableDropdown
            data-testid="expiry-month"
            v-model="newCreditCard.expMonth"
            :config="expMonthOptions"
            rules="required"
          />
        </div>
        <div class="col-sm-2">
          <SearchableDropdown
            data-testid="expiry-year"
            v-model="newCreditCard.expYear"
            :config="expYearOptions"
            rules="required"
          />
        </div>
        <div
          v-if="isExpDateInvalid"
          class="text-danger col-sm-9 col-sm-offset-3 m-t-10"
        >
          {{ $t('ACCOUNT_BILLING_ADD_CC_INVALID_MONTH') }}
        </div>
      </div>
      <Field
        :label="$t('ACCOUNT_BILLING_ADD_CC_CC_CVV')"
        :has-tooltip="true"
        :tooltip-text="$t('ACCOUNT_BILLING_ADD_CC_CC_CVV_TT')"
      >
        <template #element>
          <ValidationProvider
            v-slot="{ classes }"
            ref="csv"
            rules="required|numeric|cvv"
            slim
          > 
            <div :class="classes">
              <div class="col-sm-2">
                <input
                  v-model="newCreditCard.csv"
                  name="csv"
                  type="text"
                  maxlength="4"
                  class="form-control"
                />
              </div>
            </div>
          </ValidationProvider>
        </template>
      </Field>
      <div class="row display-flex flex--justify-end p-t-25">
        <PrimaryButton
          :disabled="invalid || isExpDateInvalid || isAddingCreditCard"
          :show-loader-on-form="isAddingCreditCard"
          class="p-r-25 p-l-25"
        >
          <span v-t="'ACCOUNT_BILLING_ADD_CC_UPDATE_BUTTON'" />
        </PrimaryButton>
        <button
          v-t="'CANCEL'"
          class="btn btn-danger m-l-15"
          @click="onCloseModal"
        />
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { mapState, mapGetters, mapActions } from 'vuex';
import { mask } from 'vue-the-mask';
import { infoService, utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import { cardTypes } from '../../helpers/constants.js';
import messages from '../../locales';
import sharedMessages from '@/core/locales';
import nzToggle from '@/core/components/nzToggle/nzToggle';
import CountrySelector from '@/core/components/CountrySelector/CountrySelector';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import USStatesList from '@/data/USStatesList';

const expMonthList = _.range(1,13);

export default {
  name: 'AddCreditCardForm',
  components: {
    nzToggle,
    CountrySelector,
    SearchableDropdown
  },
  directives: { mask },
  data() {
    return {
      useNewAddress: false,
      newCreditCard: {
        cardName: '',
        customerNameOnCard: '',
        creditCardNumber: '',
        expMonth: '',
        expYear: '',
        cardType: '',
        creditCardAddress: {
          streetAddress: '',
          addressLine2: '',
          city: '',
          postalCode: '',
          countryCode: '',
          countryName: '',
          region: ''
        }
      }
    };
  },
  computed: {
    ...mapState(['currentAccount']),
    ...mapGetters('billingSettingsStore', ['addCreditCardStatus']),
    countryValue: {
      get() {
        const label = this.newCreditCard.creditCardAddress.countryName;
        const value = this.newCreditCard.creditCardAddress.countryCode;

        if (!label || !value) {
          return null;
        }

        return {
          label,
          value,
        };
      },
      set({ label, value }) {
        this.newCreditCard.creditCardAddress.countryName = label;
        this.newCreditCard.creditCardAddress.countryCode = value;
      },
    },
    stateOptions() {
      return {
        options: USStatesList,
        reduce: (option) => option.abbreviation,
        label: 'displayName',
        placeholder: this.$t('ACCOUNT_BILLING_ADD_CC_NEW_STATE_DEFAULT')
      };
    },
    expMonthOptions() {
      return {
        options: expMonthList,
        placeholder: this.$t('ACCOUNT_BILLING_ADD_CC_CC_EXP_MO_DEFAULT')
      };
    },
    expYearOptions() {
      return {
        options: infoService.getCreditCardExpirationYearList(),
        placeholder: this.$t('ACCOUNT_BILLING_ADD_CC_CC_EXP_YEAR_DEFAULT')
      };
    },
    isExpDateInvalid() {
      if (this.newCreditCard.expMonth && this.newCreditCard.expYear) {
        const expDate = moment(`${this.newCreditCard.expYear}-${this.newCreditCard.expMonth}`, 'YYYY-MM');
        return moment(expDate).isBefore(moment(), 'day');
      }
      return false;
    },
    isAddingCreditCard() {
      return this.addCreditCardStatus.status === STATUS_PENDING;
    },
    currentAddressFormatted() {
      const address = this.currentAccount?.accountAddress;
      if (!address) return '';

      return `
        ${address.streetAddress}<br/>
        ${address.city}, ${address.region} ${address.postalCode}<br/>
        ${address.countryName}
      `
    },
    cardNumberMask() {
      const defaultMask = '#### #### #### ####';
      const amexMask = '#### ###### #####';
      const extendedMask = '#### #### #### #### ####';

      // array of types to remove the mask
      const cardsExtendedMask = ['laser', 'maestro', 'unionpay'];

      // check if the current card type is one of the types to remove the mask and set boolean
      const useExtendedMask = cardsExtendedMask.includes(this.currentCardType);
      
			return this.currentCardType === 'amex' ? amexMask : useExtendedMask ? extendedMask : defaultMask;
    },
    currentCardType() {
      // Check the cardNumber against each pattern in cardTypes
      const card = cardTypes.find(cardType => cardType.pattern.test(this.newCreditCard.creditCardNumber));

      // Return the card type or '' if not found
      return card ? card.type : '';
    }
  },
  methods: {
    ...mapActions('billingSettingsStore', ['addCreditCard']),
    async onSubmit() {
      // Remove spaces from card number
      this.newCreditCard.creditCardNumber = this.newCreditCard.creditCardNumber.replace(/\s+/g, '');

      this.newCreditCard.cardType = this.currentCardType;

      let creditCard = {
        useAccountAddress: !this.useNewAddress,
        useLoginEmail: true,
        ...this.newCreditCard
      }
      if (!this.useNewAddress) {
        delete creditCard.creditCardAddress;
      }
      await this.addCreditCard(creditCard);
      if (this.addCreditCardStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$t('ACCOUNT_BILLING_ADD_CC_SUCCESS'));
        this.onCloseModal();
      }
      if (this.addCreditCardStatus.status === STATUS_ERROR) {
        utilities.errorHandler(this.addCreditCardStatus.error);
      }
    },
    toggleUseNewAddress() {
      this.useNewAddress = !this.useNewAddress;
    },
    onCloseModal() {
      this.$emit('closeModal');
    }
  },

  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>